import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  resetJobEngineerFilters,
  setJobEngineerFilters,
} from "../../store/filters/filtersSlice";
import BaseRadio from "../../components/ui/BaseRadio";
import { Checkbox, RadioGroup } from "@mui/material";
import {
  fetchCategories,
  fetchSubCategories,
  fetchVendors,
} from "../../services/api/category";
import BaseAutoSelect from "../../components/ui/BaseAutoSelect";
import { useQuery } from "@tanstack/react-query";
import BaseRange from "../../components/ui/BaseRange";
import BaseDrawer from "../../components/ui/BaseDrawer";
import BaseDatePicker from "../../components/ui/BaseDatePicker";
import dayjs from "dayjs";
import {
  fetchMaxOffersCount,
  fetchMaxRate,
} from "../../services/api/workOrder";

const FindJobFilters = ({
  open,
  onClose,
  fetchJobs,
  fetchFreelancerJobsMutation,
  currentStatus,
  reFetchJobsCount,
  isWorkOrderLoading,
  setFiltersChanged,
  hasAppliedFilters,
}) => {
  const dispatch = useDispatch();
  const [filtersApplied, setFiltersApplied] = useState(false);
  const filters = useSelector((state) => state.filters.value);

  const [localFilters, setLocalFilters] = useState({
    enableRequestCount: false,
    enableRateRange: false,
    enablePaymentType: false,
    minRate: "",
    maxRate: "",
    minRequests: "",
    maxRequests: "",
    startDate: "",
    endDate: "",
    paymentType: "",
    categoryId: "",
    vendorId: "",
    subCategoryIds: [],
  });
  const paymentType = localFilters.paymentType;

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const { data: vendors = [] } = useQuery({
    queryKey: ["vendors", localFilters.categoryId],
    queryFn: () => fetchVendors(localFilters.categoryId),
    enabled: !!localFilters.categoryId,
  });

  const { data: subCategories = [] } = useQuery({
    queryKey: ["subCategories", localFilters.vendorId],
    queryFn: () => fetchSubCategories(localFilters.vendorId),
    enabled: !!localFilters.vendorId,
  });
  const {
    data: maxRateData,
    isLoading: isMaxRateLoading,
    refetch: refetchMaxRate,
  } = useQuery({
    queryKey: ["maxRate", paymentType],
    queryFn: () => fetchMaxRate(paymentType),
    enabled: !!paymentType,
  });
  const {
    data: maxOffersData,
    isLoading: isMaxOffersLoading,
    refetch: refetchMaxOffers,
  } = useQuery({
    queryKey: ["maxOffersCount"],
    queryFn: fetchMaxOffersCount,
  });
  useEffect(() => {
    if (filters) {
      setLocalFilters({
        ...filters,
        enableRequestCount:
          filters.minRequests !== "" || filters.maxRequests !== "",
        enableRateRange: filters.minRate !== "" || filters.maxRate !== "",
        enablePaymentType: filters.paymentType !== "",
      });
    }
  }, [filters]);
  useEffect(() => {
    if (maxRateData?.maxRate !== undefined) {
      setLocalFilters((prev) => ({
        ...prev,
        minRate:
          prev.minRate === "" || prev.minRate > maxRateData.maxRate
            ? 0
            : prev.minRate,
        maxRate:
          prev.maxRate === "" || prev.maxRate > maxRateData.maxRate
            ? maxRateData.maxRate
            : prev.maxRate,
      }));
    }
  }, [maxRateData?.maxRate]);

  useEffect(() => {
    if (!paymentType) {
      setLocalFilters((prev) => ({
        ...prev,
        minRate: "",
        maxRate: "",
      }));
    }
  }, [paymentType]);

  useEffect(() => {
    if (filtersApplied) {
      if (currentStatus === "AVAILABLE") {
        fetchJobs();
      } else {
        fetchFreelancerJobsMutation();
      }
      onClose();
      setFiltersApplied(false);
    }
  }, [filtersApplied]);
  const applyFilters = async () => {
    const { enableRequestCount, enableRateRange, enablePaymentType, ...rest } =
      localFilters;

    const cleanedFilters = {
      ...rest,
      ...(enableRequestCount ? {} : { minRequests: "", maxRequests: "" }),
      ...(enableRateRange ? {} : { minRate: "", maxRate: "" }),
      ...(enablePaymentType ? {} : { paymentType: "" }),
    };

    Object.entries(cleanedFilters).forEach(([key, val]) => {
      dispatch(setJobEngineerFilters({ filterName: key, data: val }));
    });

    dispatch(
      setJobEngineerFilters({ filterName: "pagination", data: { page: 0 } })
    );
    setTimeout(() => {
      reFetchJobsCount();
      setFiltersApplied(true);
    }, 0);
  };

  const handleResetFilters = () => {
    dispatch(resetJobEngineerFilters({ status: currentStatus }));
    setLocalFilters((prev) => ({
      ...prev,
      enableRequestCount: false,
      enableRateRange: false,
      enablePaymentType: false,
      minRate: "",
      maxRate: "",
      minRequests: "",
      maxRequests: "",
      startDate: "",
      endDate: "",
      paymentType: "",
      categoryId: "",
      vendorId: "",
      subCategoryIds: [],
    }));
    setFiltersChanged(true);
  };
  const hasFilters = !!(
    localFilters?.startDate ||
    localFilters?.endDate ||
    localFilters?.categoryId ||
    localFilters?.vendorId ||
    localFilters?.subCategoryIds?.length ||
    (localFilters?.enablePaymentType && localFilters?.paymentType) ||
    (localFilters?.enableRateRange &&
      (localFilters?.minRate || localFilters?.maxRate)) ||
    (localFilters?.enableRequestCount &&
      (localFilters?.minRequests !== null ||
        localFilters?.maxRequests !== null))
  );
  return (
    <BaseDrawer
      open={open}
      onClose={onClose}
      onApply={applyFilters}
      title="Filters"
      saveButtonText="Apply Filters"
      isLoading={isWorkOrderLoading}
      enabled={hasFilters}
    >
      <div>
        <div className="mb-4 grid grid-cols-2 gap-4 items-center">
          <div>
            <label className="block text-sm text-[#999999]">Start Date</label>
            <BaseDatePicker
              className="border-none w-full"
              type="date"
              id="startDate"
              name="startDate"
              maxDate={
                localFilters.endDate ? dayjs(localFilters.endDate) : undefined
              }
              value={
                localFilters.startDate ? dayjs(localFilters.startDate) : null
              }
              onChange={(event) => {
                const selectedDate = dayjs(event)
                  .utcOffset(0, true)
                  .format("YYYY-MM-DD");
                setLocalFilters({ ...localFilters, startDate: selectedDate });
              }}
              slotProps={{
                textField: { sx: { ...datePickerStyles } },
              }}
            />
          </div>

          <div>
            <label className="block text-sm text-[#999999]">End Date</label>
            <BaseDatePicker
              className="border-none"
              type="date"
              id="endDate"
              name="endDate"
              minDate={
                localFilters.startDate
                  ? dayjs(localFilters.startDate)
                  : undefined
              }
              value={localFilters.endDate ? dayjs(localFilters.endDate) : null}
              onChange={(event) => {
                const selectedDate = dayjs(event)
                  .utcOffset(0, true)
                  .format("YYYY-MM-DD");
                setLocalFilters({ ...localFilters, endDate: selectedDate });
              }}
              slotProps={{
                textField: { sx: { ...datePickerStyles } },
              }}
            />
          </div>
        </div>

        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Technology Stacks"
            name="categoryId"
            placeholder="Select Technology Stack"
            options={
              categories?.data?.map((c) => ({
                label: c.category,
                value: c.id,
              })) || []
            }
            value={localFilters.categoryId}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                categoryId: e.target.value,
                vendorId: "",
                subCategoryIds: [],
              })
            }
            isClearable
          />
        </div>
        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Category"
            name="vendorId"
            placeholder="Select Category"
            options={
              vendors?.data?.map((v) => ({ label: v.name, value: v.id })) || []
            }
            value={localFilters.vendorId}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                vendorId: e.target.value,
                subCategoryIds: [],
              })
            }
            isClearable
          />
        </div>
        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Skills"
            name="subCategoryIds"
            multiple
            placeholder="Select Skills"
            options={
              subCategories?.data?.map((s) => ({
                label: s.subCategory,
                value: s.id,
              })) || []
            }
            value={localFilters.subCategoryIds}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                subCategoryIds: e.target.value,
              })
            }
            isClearable
          />
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm mb-2 text-[#999999]">
            Request Count
          </label>
          <Checkbox
            readonly
            color="primary"
            size="small"
            checked={localFilters.enableRequestCount}
            onChange={(e) =>
              setLocalFilters((prev) => ({
                ...prev,
                enableRequestCount: e.target.checked,
              }))
            }
          />
        </div>

        <div
          className={`ml-3 mb-6 ${
            !localFilters.enableRequestCount
              ? "opacity-50 pointer-events-none"
              : ""
          }`}
        >
          <BaseRange
            min={0}
            max={maxOffersData?.maxOffers ?? 100}
            disabled={!localFilters.enableRequestCount}
            value={[
              localFilters.minRequests !== "" ? localFilters.minRequests : 0,
              localFilters.maxRequests !== ""
                ? localFilters.maxRequests
                : maxOffersData?.maxOffers ?? 100,
            ]}
            valueLabelDisplay="on"
            onChange={(e, newValue) => {
              const [minRequests, maxRequests] = newValue;
              setLocalFilters((prev) => ({
                ...prev,
                minRequests,
                maxRequests,
              }));
            }}
          />
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm mb-2 text-[#999999]">
            Payment Type
          </label>
          <Checkbox
            color="primary"
            size="small"
            checked={localFilters.enablePaymentType}
            onChange={(e) =>
              setLocalFilters((prev) => ({
                ...prev,
                enablePaymentType: e.target.checked,
              }))
            }
          />
        </div>

        <div
          className={`ml-1 mb-6 ${
            !localFilters.enablePaymentType
              ? "opacity-50 pointer-events-none"
              : ""
          }`}
        >
          <RadioGroup
            name="paymentType"
            value={localFilters.paymentType}
            onChange={(e) => {
              const newValue = e.target.value;
              setLocalFilters((prev) => ({
                ...prev,
                paymentType: prev.paymentType === newValue ? "" : newValue,
              }));
            }}
            className="grid grid-cols-4 gap-3"
          >
            <BaseRadio
              label="Hourly"
              value="HOURLY"
              name="paymentType"
              disabled={!localFilters.enablePaymentType}
            />
            <BaseRadio
              label="Fixed"
              value="FIXED"
              name="paymentType"
              disabled={!localFilters.enablePaymentType}
            />
            <BaseRadio
              label="Half Day"
              value="HALF_DAY"
              name="paymentType"
              disabled={!localFilters.enablePaymentType}
            />
            <BaseRadio
              label="Full Day"
              value="FULL_DAY"
              name="paymentType"
              disabled={!localFilters.enablePaymentType}
            />
          </RadioGroup>
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm mb-2 text-[#999999]">
            Rate Range ($)
          </label>
          <Checkbox
            color="primary"
            size="small"
            checked={localFilters.enableRateRange}
            onChange={(e) =>
              setLocalFilters((prev) => ({
                ...prev,
                enableRateRange: e.target.checked,
              }))
            }
          />
        </div>

        <div
          className={`ml-3 mb-16 ${
            !localFilters.enableRateRange
              ? "opacity-50 pointer-events-none"
              : ""
          }`}
        >
          <BaseRange
            min={0}
            max={maxRateData?.maxRate || 100}
            disabled={!localFilters.enableRateRange}
            value={[
              localFilters.minRate !== "" ? localFilters.minRate : 0,
              localFilters.maxRate !== ""
                ? localFilters.maxRate
                : maxRateData?.maxRate,
            ]}
            valueLabelDisplay="on"
            onChange={(e, newValue) => {
              const [minRate, maxRate] = newValue;
              setLocalFilters((prev) => ({ ...prev, minRate, maxRate }));
            }}
          />
        </div>
      </div>
      <div className="flex justify-left pt-4">
        <button
          type="button"
          onClick={() => {
            handleResetFilters();
            // if (hasAppliedFilters) setFiltersChanged(true);
          }}
          className={`underline text-sm font-medium ${
            hasAppliedFilters ? "text-blue-600" : "text-gray-400"
          }`}
        >
          Reset Filters
        </button>
      </div>
    </BaseDrawer>
  );
};

const datePickerStyles = {
  "& .MuiOutlinedInput-root": {
    height: "42px",
    borderRadius: "5px",
    backgroundColor: "white",
    boxShadow: "1px 1px 4px 1px #29292921",
    display: "flex",
    alignItems: "center",
    margin: "6px 1.5px",
    border: "none !important",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none !important",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none !important",
    boxShadow: "none",
  },
  "& .MuiInputBase-input": {
    padding: "4px 10px",
    fontSize: "13px",
    height: "18px",
    lineHeight: "1.4",
  },
  "& .MuiIconButton-root": {
    padding: "4px",
    backgroundColor: "white !important",
    borderLeft: "none !important",
  },
};

export default FindJobFilters;
